import { useState, useEffect, useMemo, useCallback } from 'react';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { getAllOrganizationGroupMembers } from '@/repository/OrganizationDAO';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
  IconButton,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { GridToolbar } from '@mui/x-data-grid/internals';
import { NavigatorDataGrid } from '@/components/data/NavigatorDataGrid';
import RoleSelector from '@/components/selectors/RoleSelector';
import ConfirmModal from '@/components/modals/utils/ConfirmModal';
import LoadingData from '@/components/loading/LoadingData';
import Loading from '@/components/loading/Loading';
import {
  useToast,
  ToastType,
} from '@/context_providers/toast_context/ToastContext';
import { deleteUserViaAdmin } from '@/repository/UserDAO';
import COLORS from '@/resources/style/Themes';
import { useHistory } from 'react-router-dom';
import { Delete } from '@mui/icons-material';
import { OrgUser } from '@/models/User/OrgUser';
import ErrorComponent from '@/components/utils/ErrorComponent';
import { useSafeguardAdminSignalRContext } from '@/context_providers/signal_r_context/SafeguardAdminSignalRListener';
import { useUserContext } from '@/context_providers/user_context/UserProvider';
import APIErrorCodes from '@/utils/error_codes/APIErrorCodes';

import { useDashboardActions } from '@/stores/map/dashboardStore';
import UserDashboard from '@/views/UserViews/home/user_dashboard/UserDashboard';
import Group from '@/models/Organization/Group';
import {
  setGroupUserStatuses,
  useAllGroupUserStatuses,
} from '@/context_providers/group_user_context/OrgGroupUserStatusStore';
import { getAllGroupUserStatusesFromOrg } from '@/repository/UserStatusDAO';
import {
  enablePrototyper,
  disablePrototyper,
} from '@/repository/PrototypeFirmwareDAO';
import {
  buildPoolCounts,
  getPoolPurchased,
  getPoolUsage,
} from '@/utils/organization/orgSeatAvailability';
import { SubscriptionTier } from '@/../generated-client/models';
import { getTierLabelKey } from '@/utils/entitlements/tierLabels';

const GroupManagement = ({ selectedGroup }: { selectedGroup: string }) => {
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmTitle, setConfirmTitle] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<OrgUser>();

  const [allGroups, setAllGroups] = useState<Group[]>([]);
  const [userToHighlight, setUserToHighlight] = useState<string | null>(null);
  const [loadingAPIData, setLoadingAPIData] = useState(false);
  const [apiError, setApiError] = useState(false);
  const { organization, pvdConfigurations, emergencyConfigurations } = useSafeguardAdminSignalRContext();
  const { showToast } = useToast();
  const history = useHistory();
  const dashboardActions = useDashboardActions();

  const { isSafeguardDev } = useUserContext();
  const [togglingAuthId, setTogglingAuthId] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<
    'remove' | 'enablePrototyper' | 'disablePrototyper' | null
  >(null);

  const groupStatuses = useAllGroupUserStatuses();

  /**
   * We want to grab the statuses for the group
   * and assign them to the OrgGroupUserStatusStore
   */
  useEffect(() => {
    const loadGroupUserStatuses = async () => {
      if (!organization) return;

      try {
        const groupUserStatusesArray = await getAllGroupUserStatusesFromOrg(
          organization.org_code,
        );

        // Populate the store with each group's data
        groupUserStatusesArray.forEach((groupUserStatuses) => {
          setGroupUserStatuses(groupUserStatuses.group_name, groupUserStatuses);
        });
      } catch (error) {
        console.error('Failed to load group user statuses:', error);
      }
    };

    loadGroupUserStatuses();
  }, [organization]);

  useEffect(() => {
    if (userToHighlight != null) {
      setTimeout(() => {
        setUserToHighlight(null);
      }, 2000);
    }
  }, [userToHighlight]);

  useEffect(() => {
    const handlePageRefresh = () => {
      history.push(`/UserManagement/${encodeURIComponent(selectedGroup)}`);
    };
    window.addEventListener('beforeunload', handlePageRefresh);

    return () => window.removeEventListener('beforeunload', handlePageRefresh);
  }, [history, selectedGroup]);

  useEffect(() => {
    const fetchAllGroups = async () => {
      if (organization) {
        try {
          const groupPromises = Object.entries(organization.groups).map(
            async ([groupName, groupValue]) => {
              const allUsersData = await getAllOrganizationGroupMembers(
                organization.org_code,
                organization.id,
                groupName,
              );

              const users = allUsersData.map((contact) => {
                return new OrgUser({
                  already_accepted_spec_etag:
                    contact.already_accepted_spec_etag,
                  auth_id: contact.auth_id,
                  first_name: contact.first_name,
                  last_name: contact.last_name,
                  org_user_azure_id: contact.id,
                  phone_number: contact.phone_number,
                  role: contact.role,
                  last_interaction_timestamp:
                    contact.last_interaction_timestamp,
                  id: contact.id,
                  language_code: contact.language_code,
                  location_preference: contact.location_preference,
                  object_type: contact.object_type,
                  org_code: contact.org_code,
                  unique_key: contact.unique_key,
                  device_location_permission:
                    contact.device_location_permission,
                  is_prototyper: contact.is_prototyper ?? false,
                  unified_config_id: contact.unified_config_id,
                  pvd_config_id: contact.pvd_config_id ?? null,
                  emergency_config_id: contact.emergency_config_id ?? null,
                  already_accepted_pvd_etag:
                    contact.already_accepted_pvd_etag ?? null,
                  already_accepted_emergency_etag:
                    contact.already_accepted_emergency_etag ?? null,
                  should_force_apply_config:
                    contact.should_force_apply_config ?? false,
                  should_force_apply_pvd_config:
                    contact.should_force_apply_pvd_config ?? false,
                  should_force_apply_emergency_config:
                    contact.should_force_apply_emergency_config ?? false,
                });
              });
              return new Group({
                group_id: groupValue.group_id,
                group_name: groupName,
                group_value: users,
                connect_user_auth_ids: groupValue.connect_user_auth_ids,
                ops_user_auth_ids: groupValue.ops_user_auth_ids,
                respond_user_auth_ids: groupValue.respond_user_auth_ids,
                allow_group_location_tracking:
                  groupValue.allow_group_location_tracking,
              });
            },
          );
          const allGroups = (await Promise.all(groupPromises)).filter(
            (g) => g !== null,
          );
          if (allGroups.length > 0) {
            const sortedGroups = allGroups.sort((a, b) => {
              if (a.group_name === 'Default') return -1;
              if (b.group_name === 'Default') return 1;
              return a.group_name.localeCompare(b.group_name ?? '');
            });
            setAllGroups(sortedGroups);
          } else {
            setApiError(true);
          }
        } catch {
          setApiError(true);
        }
      }
    };

    fetchAllGroups();
  }, [organization]);

  // Find the selected group from allGroups
  const selectedGroupData = allGroups.find(
    (group) => group.group_name === selectedGroup,
  );

  const removeUser = async () => {
    const orgUserAzureId = selectedUser?.org_user_azure_id || selectedUser?.id;
    if (!orgUserAzureId) {
      console.error('The user does not have an org_user_azure_id');
      showToast(
        "That user doesn't seem to exist. Try refreshing your page and trying again",
        ToastType.DANGER,
      );
      return;
    }
    setLoadingAPIData(true);
    setShowConfirmModal(false);
    try {
      await deleteUserViaAdmin(
        organization.org_code,
        orgUserAzureId,
        organization.id,
      );
      showToast(
        `Successfully removed ${selectedUser?.first_name} ${selectedUser?.last_name} from ${organization.company_name}`,
        'success',
        true,
      );
    } catch {
      showToast(
        `Failed to remove ${selectedUser?.first_name} ${selectedUser?.last_name} from ${organization.company_name}. Please try again.`,
        'danger',
        true,
      );
    } finally {
      setLoadingAPIData(false);
    }
  };

  const handleEnablePrototyper = async () => {
    if (!selectedUser?.auth_id) return;
    setShowConfirmModal(false);
    setTogglingAuthId(selectedUser.auth_id);
    try {
      await enablePrototyper(selectedUser.auth_id);
      setAllGroups((prev) =>
        prev.map((g) => ({
          ...g,
          group_value: g.group_value.map((u) =>
            u.auth_id === selectedUser.auth_id
              ? (Object.assign(Object.create(Object.getPrototypeOf(u)), u, {
                  is_prototyper: true,
                }) as OrgUser)
              : u,
          ),
        })),
      );
      showToast(
        `${selectedUser.first_name} ${selectedUser.last_name} is now a prototyper.`,
        ToastType.SUCCESS,
      );
    } catch {
      showToast(
        'Failed to update prototyper status. Please try again.',
        ToastType.DANGER,
      );
    } finally {
      setTogglingAuthId(null);
      setPendingAction(null);
    }
  };

  const handleDisablePrototyper = async () => {
    if (!selectedUser?.auth_id) return;
    setShowConfirmModal(false);
    setTogglingAuthId(selectedUser.auth_id);
    try {
      await disablePrototyper(selectedUser.auth_id);
      setAllGroups((prev) =>
        prev.map((g) => ({
          ...g,
          group_value: g.group_value.map((u) =>
            u.auth_id === selectedUser.auth_id
              ? (Object.assign(Object.create(Object.getPrototypeOf(u)), u, {
                  is_prototyper: false,
                }) as OrgUser)
              : u,
          ),
        })),
      );
      showToast(
        `Removed prototyper access from ${selectedUser.first_name} ${selectedUser.last_name}.`,
        ToastType.SUCCESS,
      );
    } catch {
      showToast(
        'Failed to update prototyper status. Please try again.',
        ToastType.DANGER,
      );
    } finally {
      setTogglingAuthId(null);
      setPendingAction(null);
    }
  };

  /**
   *
   * @param {number} timestamp
   * @returns
   */
  function formatInteractionDate(timestamp: number) {
    const dateObj = new Date(timestamp);

    if (isNaN(dateObj.getTime())) {
      return 'No previous interaction';
    }

    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  const openRemoveUserConfirm = useCallback(
    (user: OrgUser) => {
      setConfirmTitle(`Remove ${user.first_name} ${user.last_name}`);
      setConfirmMessage(
        `Are you sure you would like to remove ${user.first_name} ${user.last_name} from ${organization.company_name}?`,
      );
      setSelectedUser(user);
      setPendingAction('remove');
      setShowConfirmModal(true);
    },
    [organization],
  );

  const openEnablePrototyperConfirm = useCallback((user: OrgUser) => {
    setConfirmTitle(`Make ${user.first_name} ${user.last_name} a Prototyper`);
    setConfirmMessage(
      `Are you sure you want to grant ${user.first_name} ${user.last_name} prototype firmware access? They will see the prototype firmware menu in the Compass app.`,
    );
    setSelectedUser(user);
    setPendingAction('enablePrototyper');
    setShowConfirmModal(true);
  }, []);

  const openDisablePrototyperConfirm = useCallback((user: OrgUser) => {
    setConfirmTitle(
      `Remove Prototyper Access from ${user.first_name} ${user.last_name}`,
    );
    setConfirmMessage(
      `Are you sure you want to revoke ${user.first_name} ${user.last_name}'s prototype firmware access? Any Compass device currently running prototype firmware will be prompted to revert to production firmware the next time it connects to their app.`,
    );
    setSelectedUser(user);
    setPendingAction('disablePrototyper');
    setShowConfirmModal(true);
  }, []);

  const columns = useMemo<GridColDef<OrgUser>[]>(() => {
    if (!organization) {
      return [];
    }

    const baseColumns: GridColDef<OrgUser>[] = [
      {
        field: 'userName',
        headerName: 'User Name',
        headerClassName: 'first',
        type: 'string',
        minWidth: 175,
        flex: 1,
        display: 'flex',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (_, row) => `${row.last_name} ${row.first_name}`.trim(),
        renderCell: ({ row }) => (
          <Box
            onClick={() => {
              dashboardActions.openDashboard(row.auth_id);
            }}
            sx={{
              cursor: 'pointer',
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {row.first_name} {row.last_name}
          </Box>
        ),
      },
      {
        field: 'phone_number',
        headerName: 'Phone',
        type: 'string',
        minWidth: 140,
        flex: 0.8,
        display: 'flex',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'lastInteraction',
        headerName: 'Last Interaction',
        type: 'number',
        minWidth: 180,
        flex: 1,
        display: 'flex',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (_, row) => row.last_interaction_timestamp ?? 0,
        renderCell: ({ row }) =>
          row.last_interaction_timestamp
            ? formatInteractionDate(row.last_interaction_timestamp)
            : 'No previous interaction',
      },
      {
        field: 'role',
        headerName: 'Role',
        type: 'string',
        minWidth: 175,
        flex: 0.8,
        display: 'flex',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (_, row) => row.role,
        renderCell: ({ row }) => (
          <RoleSelector
            orgAzureId={organization.id}
            orgCode={organization.org_code}
            roleList={Object.keys(
              organization.roles_with_features_default || {},
            )}
            user={row}
            userToHighlight={setUserToHighlight}
          />
        ),
      },
      {
        field: 'removeUser',
        headerName: 'Remove User',
        sortable: false,
        filterable: false,
        minWidth: 130,
        flex: 0.5,
        display: 'flex',
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => (
          <IconButton
            sx={{
              borderRadius: '10px',
              width: '40px',
              height: '40px',
              backgroundColor: COLORS.PINK,
            }}
            onClick={(event) => {
              event.stopPropagation();
              openRemoveUserConfirm(row);
            }}
          >
            <Delete htmlColor={COLORS.DARK_RED} width={20} height={20} />
          </IconButton>
        ),
      },
    ];

    if (isSafeguardDev) {
      baseColumns.push({
        field: 'prototyper',
        headerName: 'Prototyper',
        minWidth: 240,
        flex: 1.2,
        display: 'flex',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (_, row) => (row.is_prototyper ? 1 : 0),
        renderCell: ({ row }) =>
          row.is_prototyper ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip label="Prototyper" color="complete" size="small" />
              <Button
                variant="contained"
                size="small"
                disabled={togglingAuthId === row.auth_id}
                sx={{
                  backgroundColor: COLORS.PINK,
                  color: COLORS.DARK_RED,
                  '&:hover': { backgroundColor: COLORS.HOVER_RED },
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  openDisablePrototyperConfirm(row);
                }}
                startIcon={
                  togglingAuthId === row.auth_id ? (
                    <CircularProgress size={14} />
                  ) : undefined
                }
              >
                Remove Prototyper
              </Button>
            </Box>
          ) : (
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={togglingAuthId === row.auth_id}
              onClick={(event) => {
                event.stopPropagation();
                openEnablePrototyperConfirm(row);
              }}
              startIcon={
                togglingAuthId === row.auth_id ? (
                  <CircularProgress size={14} />
                ) : undefined
              }
            >
              Make Prototyper
            </Button>
          ),
      });
    }

    return baseColumns;
  }, [
    dashboardActions,
    isSafeguardDev,
    openDisablePrototyperConfirm,
    openEnablePrototyperConfirm,
    openRemoveUserConfirm,
    organization,
    togglingAuthId,
  ]);

  const renderGroup = () => {
    if (!selectedGroupData) return null; // If no selected group data, return null
    const { group_name, group_value, ops_user_auth_ids, respond_user_auth_ids } =
      selectedGroupData;
    const opsUsedInGroup = (ops_user_auth_ids ?? []).length;
    const respondUsedInGroup = (respond_user_auth_ids ?? []).length;

    // Pools are org-wide, not per-group — purchased and org-wide-used counts
    // don't vary by which group is currently selected.
    const orgPoolCounts = buildPoolCounts(organization);
    const orgOpsUsed = getPoolUsage(orgPoolCounts, SubscriptionTier.Ops);
    const orgRespondUsed = getPoolUsage(orgPoolCounts, SubscriptionTier.Respond);
    const orgOpsPurchased = getPoolPurchased(orgPoolCounts, SubscriptionTier.Ops);
    const orgRespondPurchased = getPoolPurchased(orgPoolCounts, SubscriptionTier.Respond);
    const opsLabel = getTierLabelKey(SubscriptionTier.Ops);
    const respondLabel = getTierLabelKey(SubscriptionTier.Respond);

    return (
      <Box sx={{ p: 1 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          {group_name} Group
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
          {organization?.company_name} total subscriptions:{' '}
          <span style={{ fontWeight: 'normal' }}>
            {opsLabel} {orgOpsUsed}/{orgOpsPurchased} &middot; {respondLabel}{' '}
            {orgRespondUsed}/{orgRespondPurchased}
          </span>
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
          {group_name} group subscriptions used:{' '}
          <span style={{ fontWeight: 'normal' }}>
            {opsLabel} {opsUsedInGroup}/{orgOpsPurchased} &middot; {respondLabel}{' '}
            {respondUsedInGroup}/{orgRespondPurchased}
          </span>
        </Typography>
        {renderUsersInAGroup(group_value)}
      </Box>
    );
  };

  const renderUsersInAGroup = (groupValue: Array<OrgUser>) => {
    const rows = [...groupValue].sort((a, b) =>
      a.last_name.localeCompare(b.last_name),
    );

    return (
      <NavigatorDataGrid<OrgUser>
        rows={rows}
        columns={columns}
        getRowId={(row) => row.auth_id}
        getRowClassName={(params) =>
          params.row.auth_id === userToHighlight ? 'highlighted-user-row' : ''
        }
        showToolbar={true}
        slots={{
          toolbar: GridToolbar,
          noRowsOverlay: () => (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              No users
            </Box>
          ),
        }}
        slotProps={{
          toolbar: {
            printOptions: { disableToolbarButton: true },
            csvOptions: { disableToolbarButton: true },
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 },
          },
        }}
        sx={{
          '& .highlighted-user-row': {
            backgroundColor: `${COLORS.LIGHT_GREEN} !important`,
          },
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: COLORS.SAFEGUARD_BLUE,
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            color: COLORS.WHITE,
            fontWeight: 'bold',
          },
          '& .MuiDataGrid-sortIcon': {
            color: `${COLORS.WHITE} !important`,
          },
          '& .MuiDataGrid-menuIcon .MuiIconButton-root': {
            color: `${COLORS.WHITE} !important`,
          },
          '& .MuiDataGrid-columnSeparator': {
            color: `${COLORS.WHITE} !important`,
          },
        }}
      />
    );
  };

  return (
    <>
      {apiError ? (
        <ErrorComponent error={APIErrorCodes.FETCH_ERROR} />
      ) : !organization || !selectedGroupData ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50vh',
          }}
        >
          <LoadingData show={true} />
        </Box>
      ) : (
        renderGroup()
      )}
      <LoadingData show={loadingAPIData} />
      <UserDashboard
        groups={allGroups}
        pvdConfigurations={pvdConfigurations ?? undefined}
        organization={organization}
        allGroupUserStatuses={groupStatuses}
      />
      <ConfirmModal
        show={showConfirmModal}
        confirm={() => {
          if (pendingAction === 'enablePrototyper') return handleEnablePrototyper();
          if (pendingAction === 'disablePrototyper') return handleDisablePrototyper();
          return removeUser();
        }}
        onHide={() => {
          setSelectedUser(undefined);
          setShowConfirmModal(false);
          setPendingAction(null);
        }}
        confirmMessage={confirmMessage}
        confirmTitle={confirmTitle}
      />
    </>
  );
};

const ProtectedGroupManagement = withAuthenticationRequired(GroupManagement, {
  onRedirecting: () => <Loading loadingText={'Loading...'} />,
});

export default ProtectedGroupManagement;
